import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { BookOpen, Video, FileText, Play } from 'lucide-react';
import ProfessionalPDFViewer from '../ProfessionalPDFViewer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COURSE_TYPES = {
  ABAT: 'ABAT',
  'QASP-S': 'QASP-S',
  QBA: 'QBA'
};

const MyCourses = () => {
  const { t } = useTranslation();
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/student/my-courses`);
      setContents(response.data.contents);
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const viewContent = (content) => {
    setSelectedContent(content);
    setShowViewDialog(true);
  };

  const getContentsByCourseType = (type) => {
    return contents.filter(c => c.course_type === type);
  };

  const renderContentCard = (content) => (
    <Card key={content.id} className="shadow-lg hover:shadow-xl transition-shadow">
      <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
        <CardTitle className="flex items-center gap-2 text-lg">
          {content.file_type === 'video' ? (
            <Video className="h-5 w-5 text-purple-600" />
          ) : (
            <FileText className="h-5 w-5 text-pink-600" />
          )}
          {content.title}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4">
        <p className="text-sm text-gray-600 mb-4 line-clamp-3">{content.description}</p>
        
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
          <span>{content.file_type === 'video' ? t('video') : 'PowerPoint'}</span>
          <span>•</span>
          <span>{new Date(content.created_at).toLocaleDateString()}</span>
        </div>

        <Button
          onClick={() => viewContent(content)}
          className="w-full bg-gradient-to-r from-purple-600 to-pink-600"
          size="sm"
        >
          <Play className="h-4 w-4 mr-2" />
          {t('view')}
        </Button>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <BookOpen className="h-8 w-8" />
          {t('myCourses') || 'دوراتي'}
        </h2>
        <p className="text-gray-600 mt-1">
          {t('accessYourCourses') || 'الوصول إلى المحتوى التعليمي الخاص بك'}
        </p>
      </div>

      <Tabs defaultValue="ABAT">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="ABAT">ABAT</TabsTrigger>
          <TabsTrigger value="QASP-S">QASP-S</TabsTrigger>
          <TabsTrigger value="QBA">QBA</TabsTrigger>
        </TabsList>

        {Object.keys(COURSE_TYPES).map((type) => (
          <TabsContent key={type} value={type} className="mt-6">
            {getContentsByCourseType(type).length === 0 ? (
              <Card>
                <CardContent className="pt-6 text-center text-gray-500">
                  {t('noCoursesAvailable') || 'لا يوجد محتوى متاح لك في هذه الدورة'}
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {getContentsByCourseType(type).map(renderContentCard)}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* View Content Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-[100vw] w-[100vw] h-[100vh] max-h-[100vh] p-0 gap-0 overflow-hidden m-0 rounded-none flex flex-col">
          <DialogHeader className="px-4 py-2 border-b flex-shrink-0 bg-white">
            <DialogTitle className="text-base font-semibold">
              {selectedContent?.course_type} - {selectedContent?.title}
            </DialogTitle>
          </DialogHeader>
          
          {selectedContent && (
            <div className="flex-1 overflow-hidden" style={{ height: 'calc(100vh - 48px)' }}>
              {selectedContent.file_type === 'video' ? (
                <div className="w-full h-full bg-black flex items-center justify-center">
                  <video
                    controls
                    controlsList="nodownload"
                    className="max-w-full max-h-full"
                    src={`${API}/courses/stream/${selectedContent.id}?token=${localStorage.getItem('token')}`}
                    onContextMenu={(e) => e.preventDefault()}
                  >
                    Your browser does not support video playback.
                  </video>
                </div>
              ) : (
                <ProfessionalPDFViewer 
                  fileUrl={`${API}/courses/stream-pdf/${selectedContent.id}?token=${localStorage.getItem('token')}`}
                  canDownload={false}
                />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyCourses;
